import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Pencil,
  AlertTriangle,
  Info,
  AlertCircle,
  Shield,
  HelpCircle,
} from 'lucide-react';

import { cn } from '../lib/utils';
import {
  ScopeResponseRoom,
  ScopeResponseLineItem,
  ScopeResponseCodeReq,
  ValidationIssue,
} from '../types';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { ProvenanceBadge } from './ProvenanceBadge';

// ================================================================= //
//          Line Item Status Type                                      //
// ================================================================= //

export type LineItemStatus = 'pending' | 'accepted' | 'rejected' | 'edited';

// ================================================================= //
//          Confidence Badge                                           //
// ================================================================= //

export const ConfidenceBadge: React.FC<{ confidence: string }> = ({ confidence }) => {
  const styles: Record<string, string> = {
    high: 'bg-green-100 text-green-800 border-green-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    low: 'bg-red-100 text-red-800 border-red-200',
  };

  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize', styles[confidence] || styles.medium)}>
      {confidence}
    </span>
  );
};

// ================================================================= //
//          Summary Stats Bar                                          //
// ================================================================= //

interface SummaryStatsProps {
  rooms: ScopeResponseRoom[];
  generalItems: ScopeResponseLineItem[];
  statuses: Record<string, LineItemStatus>;
  stats?: Record<string, any>;
}

export const SummaryStatsBar: React.FC<SummaryStatsProps> = ({ rooms, generalItems, statuses, stats }) => {
  const allItems = [...rooms.flatMap((r) => r.items), ...generalItems];
  const total = allItems.length;
  const highConfidence = allItems.filter((i) => i.confidence === 'high').length;
  const needsReview = allItems.filter((i) => i.confidence === 'low').length;
  const accepted = Object.values(statuses).filter((s) => s === 'accepted').length;
  const rejected = Object.values(statuses).filter((s) => s === 'rejected').length;

  const statCards = [
    { label: 'Total Items', value: stats?.total_items ?? total, color: 'text-foreground' },
    { label: 'High Confidence', value: stats?.high_confidence ?? highConfidence, color: 'text-green-700' },
    { label: 'Needs Review', value: stats?.needs_review ?? needsReview, color: 'text-red-700' },
    { label: 'Accepted', value: accepted, color: 'text-green-700' },
    { label: 'Rejected', value: rejected, color: 'text-red-700' },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
      {statCards.map((stat) => (
        <div key={stat.label} className="rounded-lg border bg-white p-3 text-center shadow-sm">
          <p className={cn('text-2xl font-bold', stat.color)}>{stat.value}</p>
          <p className="text-xs text-muted-foreground">{stat.label}</p>
        </div>
      ))}
    </div>
  );
};

// ================================================================= //
//          Questions Section                                          //
// ================================================================= //

interface QuestionsSectionProps {
  questions: string[];
}

export const QuestionsSection: React.FC<QuestionsSectionProps> = ({ questions }) => {
  if (questions.length === 0) return null;

  return (
    <Card className="border-yellow-200 bg-yellow-50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center text-lg text-yellow-800">
          <HelpCircle className="mr-2 h-5 w-5" />
          Clarification Questions ({questions.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {questions.map((q, i) => (
            <li key={i} className="rounded-md border border-yellow-200 bg-white p-3">
              <p className="text-sm text-foreground">{q}</p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

// ================================================================= //
//          Warnings Section                                           //
// ================================================================= //

interface WarningsSectionProps {
  warnings: string[];
}

export const WarningsSection: React.FC<WarningsSectionProps> = ({ warnings }) => {
  if (warnings.length === 0) return null;

  return (
    <Card className="border-orange-200 bg-orange-50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center text-lg text-orange-800">
          <AlertTriangle className="mr-2 h-5 w-5" />
          Warnings ({warnings.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {warnings.map((w, i) => (
            <li key={i} className="rounded-md border border-orange-200 bg-white p-3 text-sm text-foreground">
              {w}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

// ================================================================= //
//          Validation Issues Panel                                     //
// ================================================================= //

interface ValidationPanelProps {
  issues: ValidationIssue[];
}

export const ValidationPanel: React.FC<ValidationPanelProps> = ({ issues }) => {
  if (issues.length === 0) return null;

  const iconMap: Record<string, React.ReactNode> = {
    error: <AlertCircle className="h-4 w-4 text-red-600" />,
    warning: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
    info: <Info className="h-4 w-4 text-blue-600" />,
  };

  const bgMap: Record<string, string> = {
    error: 'bg-red-50 border-red-200',
    warning: 'bg-yellow-50 border-yellow-200',
    info: 'bg-blue-50 border-blue-200',
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center text-lg">
          <AlertTriangle className="mr-2 h-5 w-5 text-yellow-600" />
          Validation Issues ({issues.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {issues.map((issue, i) => {
            const isAi = issue.source === 'ai_reviewer';
            return (
              <li
                key={i}
                data-source={issue.source || 'validator'}
                className={cn('flex items-start gap-2 rounded-md border p-3', bgMap[issue.severity] || bgMap.info)}
              >
                {iconMap[issue.severity] || iconMap.info}
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{issue.message}</p>
                    {isAi && (
                      <span
                        className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800"
                        title={`AI reviewer (${issue.model_used || 'sonnet'})`}
                      >
                        AI reviewer
                      </span>
                    )}
                    {isAi && issue.needs_human_review && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
                        title="Confidence remained low even after re-evaluation — estimator should verify"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Review needed
                      </span>
                    )}
                    {isAi && issue.category && (
                      <span className="text-[10px] font-mono text-muted-foreground">{issue.category}</span>
                    )}
                  </div>
                  {issue.suggestion && (
                    <p className="mt-1 text-xs text-muted-foreground">Suggestion: {issue.suggestion}</p>
                  )}
                  {issue.room && (
                    <p className="mt-0.5 text-xs text-muted-foreground">Room: {issue.room}</p>
                  )}
                  {issue.xactimate_code && (
                    <p className="mt-0.5 text-xs text-muted-foreground font-mono">Code: {issue.xactimate_code}</p>
                  )}
                  {isAi && typeof issue.confidence === 'number' && (
                    <p className="mt-0.5 text-[10px] text-muted-foreground">
                      Confidence: {(issue.confidence * 100).toFixed(0)}%
                      {issue.model_used && ` • ${issue.model_used}`}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

// ================================================================= //
//          Code Requirements Panel                                    //
// ================================================================= //

interface CodeRequirementsPanelProps {
  requirements: ScopeResponseCodeReq[];
}

export const CodeRequirementsPanel: React.FC<CodeRequirementsPanelProps> = ({ requirements }) => {
  if (requirements.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center text-lg">
          <Shield className="mr-2 h-5 w-5 text-primary" />
          Code Requirements ({requirements.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {requirements.map((req, i) => (
            <li key={i} className="rounded-md border bg-white p-3">
              <p className="text-sm font-medium text-foreground">{req.code_reference}</p>
              <p className="mt-1 text-sm text-muted-foreground">{req.description}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                [{req.severity}] Source: {req.source}
              </p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

// ================================================================= //
//          Scope Line Item Row                                        //
// ================================================================= //

interface ScopeLineItemRowProps {
  item: ScopeResponseLineItem;
  index: number;
  roomKey: string;
  status: LineItemStatus;
  onStatusChange: (key: string, status: LineItemStatus) => void;
  showSources?: boolean;
}

export const ScopeLineItemRow: React.FC<ScopeLineItemRowProps> = ({
  item,
  index,
  roomKey,
  status,
  onStatusChange,
  showSources = false,
}) => {
  const itemKey = `${roomKey}-${index}`;

  const statusBg: Record<LineItemStatus, string> = {
    pending: '',
    accepted: 'bg-green-50',
    rejected: 'bg-red-50 opacity-60',
    edited: 'bg-blue-50',
  };

  return (
    <div className={cn('grid grid-cols-12 items-center gap-2 border-b p-3 last:border-b-0 hover:bg-slate-50', statusBg[status])}>
      {/* Xactimate code */}
      <div className="col-span-2 lg:col-span-1">
        <span className="font-mono text-xs text-primary">{item.xactimate_code}</span>
      </div>

      {/* Description + source + provenance */}
      <div className="col-span-4 lg:col-span-4">
        <div className="flex items-start gap-1.5">
          <p className="text-sm font-medium text-foreground">{item.description}</p>
          {item.provenance && (
            <ProvenanceBadge provenance={item.provenance} showSources={showSources} />
          )}
        </div>
        {/* T-0403 — a propagation candidate is not a gap-finder suggestion.
            The wire still says "Gap-finder suggestion" in `review_reason`
            (assembler.py:128-131, filed for WS-SCOPE); the honest classifier
            is `source_rule_id`, so the label is derived from that here. */}
        {item.source_rule_id?.startsWith('PROP-') && (
          <span
            data-testid="propagation-candidate-badge"
            className="mt-0.5 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900"
          >
            Propagation candidate · {item.source_rule_id}
          </span>
        )}
        {item.note && <p className="text-xs text-muted-foreground">{item.note}</p>}
      </div>

      {/* Activity */}
      <div className="hidden lg:col-span-1 lg:block">
        <span className="text-xs text-muted-foreground">{item.activity}</span>
      </div>

      {/* Quantity + Unit */}
      <div className="col-span-2 lg:col-span-2 text-right">
        <span className="font-mono text-sm">{item.quantity ?? '—'}</span>
        <span className="ml-1 text-xs text-muted-foreground">{item.unit}</span>
      </div>

      {/* Confidence */}
      <div className="col-span-2 lg:col-span-2">
        <ConfidenceBadge confidence={item.confidence} />
      </div>

      {/* Actions */}
      <div className="col-span-2 lg:col-span-2 flex justify-end gap-1">
        <button
          onClick={() => onStatusChange(itemKey, 'accepted')}
          className={cn(
            'rounded p-1.5 transition-colors',
            status === 'accepted' ? 'bg-green-200 text-green-800' : 'text-gray-400 hover:bg-green-100 hover:text-green-700'
          )}
          title="Accept"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          onClick={() => onStatusChange(itemKey, 'rejected')}
          className={cn(
            'rounded p-1.5 transition-colors',
            status === 'rejected' ? 'bg-red-200 text-red-800' : 'text-gray-400 hover:bg-red-100 hover:text-red-700'
          )}
          title="Reject"
        >
          <X className="h-4 w-4" />
        </button>
        <button
          onClick={() => onStatusChange(itemKey, 'edited')}
          className={cn(
            'rounded p-1.5 transition-colors',
            status === 'edited' ? 'bg-blue-200 text-blue-800' : 'text-gray-400 hover:bg-blue-100 hover:text-blue-700'
          )}
          title="Edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

// ================================================================= //
//          Collapsible Room Section                                    //
// ================================================================= //

interface RoomSectionProps {
  room: ScopeResponseRoom;
  roomIndex: number;
  statuses: Record<string, LineItemStatus>;
  onStatusChange: (key: string, status: LineItemStatus) => void;
  showSources?: boolean;
}

export const RoomSection: React.FC<RoomSectionProps> = ({
  room,
  roomIndex,
  statuses,
  onStatusChange,
  showSources = false,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const roomKey = `room-${roomIndex}`;
  const needsReview = room.items.filter((i) => i.confidence === 'low').length;

  return (
    <div className="rounded-lg border bg-white shadow-sm">
      {/* Room header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between p-4 text-left hover:bg-slate-50"
      >
        <div className="flex items-center gap-3">
          {isOpen ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
          <div>
            <h3 className="text-lg font-semibold text-foreground">{room.room_name}</h3>
            <span className="text-xs text-muted-foreground">{room.room_type}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{room.items.length} items</span>
          {needsReview > 0 && (
            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
              {needsReview} needs review
            </span>
          )}
        </div>
      </button>

      {/* Line items table */}
      {isOpen && (
        <div className="border-t">
          {/* Header row */}
          <div className="grid grid-cols-12 gap-2 border-b bg-slate-50 px-3 py-2 text-xs font-medium text-muted-foreground">
            <div className="col-span-2 lg:col-span-1">Code</div>
            <div className="col-span-4 lg:col-span-4">Description</div>
            <div className="hidden lg:col-span-1 lg:block">Activity</div>
            <div className="col-span-2 lg:col-span-2 text-right">Qty</div>
            <div className="col-span-2 lg:col-span-2">Confidence</div>
            <div className="col-span-2 lg:col-span-2 text-right">Actions</div>
          </div>

          {room.items.map((item, index) => (
            <ScopeLineItemRow
              key={index}
              item={item}
              index={index}
              roomKey={roomKey}
              status={statuses[`${roomKey}-${index}`] || 'pending'}
              onStatusChange={onStatusChange}
              showSources={showSources}
            />
          ))}

          {/* Room notes */}
          {room.notes.length > 0 && (
            <div className="border-t bg-slate-50 px-4 py-2">
              <p className="text-xs font-medium text-muted-foreground">Notes:</p>
              <ul className="mt-1 list-disc pl-4 text-xs text-muted-foreground">
                {room.notes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
